import { useEffect, useMemo } from 'react'
import { Button } from 'shared/ui/Button/ui/Button'
import { Typography } from 'shared/ui'
import { numberFormat } from 'shared/lib'
import type { IRowDefault } from 'shared/ui/TableNew'
import styles from './styles.module.scss'

type IUiHeaderCenterContentProps<TRow extends IRowDefault> = {
  total: number
  bulkTotal: number
  element: string
  bulkAllMode: boolean
  selectedIds: TRow['id'][]
  changeBulkAllMode: (value: boolean) => void
}

export const UiHeaderCenterContent = <TRow extends IRowDefault>({
  bulkAllMode,
  selectedIds,
  changeBulkAllMode,
  total,
  bulkTotal,
  element,
}: IUiHeaderCenterContentProps<TRow>) => {
  const correctText = total > 1 ? `${element}s are selected` : `${element} is selected`

  const selectedCountText = useMemo(
    () =>
      bulkAllMode && total === bulkTotal
        ? `All ${numberFormat({ value: total })} ${correctText}`
        : `You have selected ${numberFormat({
            value: bulkAllMode ? bulkTotal : selectedIds.length,
          })} of ${numberFormat({
            value: total,
          })} ${element}s`,
    [bulkAllMode, selectedIds.length]
  )

  const buttonText = useMemo(
    () => (bulkAllMode ? 'Clear selection' : `Select all ${element}s`),
    [bulkAllMode]
  )

  const handleClick = () => {
    changeBulkAllMode(!bulkAllMode)
  }

  useEffect(() => {
    if (total === selectedIds.length) {
      changeBulkAllMode(true)
    }
  }, [selectedIds.length, total])

  return (
    <>
      <Typography
        variant={'body-sm-medium'}
        className={styles.text}
        ariaLabel={'selectedCountText'}
      >
        {selectedCountText}
      </Typography>
      <Button
        text={buttonText}
        typeBtn='text'
        contained='secondary'
        size='small'
        intent={'positive'}
        textClassname={styles.selectAllButton}
        onClick={handleClick}
      />
    </>
  )
}
